import type { CardKitSchema } from "./cardkit.js"
import type { PromptPart } from "./content-extractor.js"
import type { ReplyRunState, ReplyTerminalState } from "../handler/reply-run-registry.js"
import type { DetailPhaseSnapshot, DetailPhaseStatus } from "../types.js"
import { cleanMarkdown, truncateMarkdown } from "./markdown.js"

// Re-export 保持其他 feishu 模块（streaming-card）的 import 路径不变。
export type { DetailPhaseSnapshot, DetailPhaseStatus }

export const STATUS_ELEMENT_ID = "reply_status"
export const SESSION_ELEMENT_ID = "reply_session"
export const REPLY_ELEMENT_ID = "reply_text"
export const DETAILS_ELEMENT_ID = "reply_details"
export const DETAILS_CONTENT_ELEMENT_ID = "reply_details_content"
export const ACTIONS_ELEMENT_ID = "reply_actions"

const DEFAULT_TITLE = "AI 回复"
// agent 输出为空时显示的 plugin UI 占位描述。
// 斜体 markdown 明示这是 plugin 状态而非 agent 内容（避免 plugin 编造代替 agent 发言）。
const EMPTY_REPLY_PLACEHOLDER = "_⏳ 等待 agent 回复_"
const MAX_TITLE_LENGTH = 72

type HeaderTemplate = "blue" | "green" | "orange" | "red" | "purple" | "grey"

export interface AbortActionValue {
  action: "abort_reply"
  runId: string
  sessionId: string
  source?: string
  cardVersion?: number
}

export interface ReplyCardAction {
  kind: "abort"
  text: string
  style: "primary" | "default" | "danger"
  disabled?: boolean
  value: AbortActionValue
}

export interface ReplyCardView {
  runId: string
  sessionId?: string
  title: string
  compactStatus: string
  replyText: string
  detailsCollapsed: boolean
  detailsMarkdown?: string
  terminalState?: ReplyTerminalState
  actions: ReplyCardAction[]
  fallbackMode: "structured" | "simple"
  headerTemplate: HeaderTemplate
}

export function normalizeReplyTitle(input: string, fallback = DEFAULT_TITLE): string {
  const normalized = input
    .replace(/\r/g, "\n")
    .replace(/\n+/g, "\n")
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean)
    ?.replace(/\s+/g, " ")
    .replace(/[`*_#>\[\]]/g, "")
    .trim()

  if (!normalized) return fallback
  if (normalized.length <= MAX_TITLE_LENGTH) return normalized
  return normalized.slice(0, MAX_TITLE_LENGTH - 1).trimEnd() + "…"
}

export function deriveReplyTitleFromParts(parts: readonly PromptPart[]): string {
  const text = parts
    .filter((part): part is Extract<PromptPart, { type: "text" }> => part.type === "text")
    .map((part) => part.text)
    .join("\n")

  const withoutQuotePrefix = text.replace(/^\[回复消息\]:[\s\S]*?---\s*/u, "")
  const withoutSpeakerPrefix = withoutQuotePrefix.replace(/^\[[^\]]+\]:\s*/u, "")
  return normalizeReplyTitle(withoutSpeakerPrefix)
}

export function buildCompactStatus(state: ReplyRunState): string {
  switch (state) {
    case "starting":
      return "⏳ 正在建立结果卡"
    case "running":
      return "⏳ 正在生成回复"
    case "completing":
      return "✅ 正在收尾"
    case "aborting":
      return "🛑 正在中断"
    case "completed":
      return "✅ 已完成"
    case "aborted":
      return "⛔ 已中断"
    case "failed":
      return "❌ 已失败"
    case "timed_out":
      return "⚠️ 已超时"
    default:
      return "⏳ 处理中"
  }
}

export function resolveHeaderTemplate(
  state: ReplyRunState,
  terminalState?: ReplyTerminalState,
): HeaderTemplate {
  const terminal = terminalState ?? toTerminalState(state)
  switch (terminal) {
    case "completed":
      return "green"
    case "failed":
      return "red"
    case "timed_out":
    case "aborted":
      return "orange"
    default:
      return "blue"
  }
}

export function toTerminalState(state: ReplyRunState): ReplyTerminalState | undefined {
  switch (state) {
    case "completed":
      return "completed"
    case "failed":
      return "failed"
    case "timed_out":
      return "timed_out"
    case "aborted":
      return "aborted"
    default:
      return undefined
  }
}

export function buildAbortAction(runId: string, sessionId: string, cardVersion = 1): ReplyCardAction {
  return {
    kind: "abort",
    text: "中断回答",
    style: "danger",
    value: {
      action: "abort_reply",
      runId,
      sessionId,
      source: "reply-card",
      cardVersion,
    },
  }
}

export function buildDetailsMarkdown(phases: Iterable<DetailPhaseSnapshot>): string | undefined {
  const sections: string[] = []

  for (const phase of phases) {
    const body = normalizeBlockMarkdown(phase.body)
    const toolSummary = Array.isArray(phase.toolSummary) && phase.toolSummary.length > 0
      ? phase.toolSummary.map((item) => `- ${item}`).join("\n")
      : ""

    if (!body && !toolSummary) continue

    const phaseSections = [
      `### ${formatPhaseIcon(phase.status)} ${phase.label}`,
      body,
      toolSummary ? `**工具进度**\n${toolSummary}` : "",
    ].filter(Boolean)

    sections.push(phaseSections.join("\n\n"))
  }

  if (sections.length === 0) return undefined
  return truncateMarkdown(sections.join("\n\n---\n\n"))
}

export function createReplyCardView(params: {
  runId: string
  sessionId?: string
  title: string
  state: ReplyRunState
  replyText?: string
  detailsMarkdown?: string
  actions?: ReplyCardAction[]
  fallbackMode?: "structured" | "simple"
  terminalState?: ReplyTerminalState
}): ReplyCardView {
  const title = normalizeReplyTitle(params.title)
  // replyText 不在这里 normalize：buildReplyMarkdown / buildSimpleFallbackText 会各自处理
  // 提前 normalize 会让 cleanMarkdown 跑两次（HTML 解析 + 代码块闭合都重复）
  const replyText = params.replyText ?? ""
  const terminalState = params.terminalState ?? toTerminalState(params.state)
  return {
    runId: params.runId,
    sessionId: params.sessionId,
    title,
    compactStatus: buildCompactStatus(params.state),
    replyText,
    detailsCollapsed: true,
    detailsMarkdown: params.detailsMarkdown,
    terminalState,
    actions: params.actions ?? [],
    fallbackMode: params.fallbackMode ?? "structured",
    headerTemplate: resolveHeaderTemplate(params.state, terminalState),
  }
}

export function buildReplyCardSchema(view: ReplyCardView): CardKitSchema {
  // 用户消息标题升到 header.title（CardKit 协议字段，载体级，创建时设定）
  // body 区只剩 status（plugin 自身状态）+ reply（agent 文本原样）+ details + actions
  const elements: Array<Record<string, unknown>> = [
    buildMarkdownElement(STATUS_ELEMENT_ID, buildStatusMarkdown(view.compactStatus)),
  ]
  const sessionMarkdown = buildSessionMarkdown(view.sessionId)
  if (sessionMarkdown) {
    elements.push(buildMarkdownElement(SESSION_ELEMENT_ID, sessionMarkdown))
  }
  elements.push(buildMarkdownElement(REPLY_ELEMENT_ID, buildReplyMarkdown(view.replyText)))

  const detailsElement = buildDetailsElement(view.detailsMarkdown)
  if (detailsElement) elements.push(detailsElement)

  const actionsElement = buildActionsElement(view.actions)
  if (actionsElement) elements.push(actionsElement)

  return {
    data: {
      schema: "2.0",
      config: {
        streaming_mode: true,
        wide_screen_mode: true,
      },
      header: {
        title: { tag: "plain_text", content: view.title.trim() || DEFAULT_TITLE },
        template: view.headerTemplate,
      },
      body: {
        elements,
      },
    },
  }
}

export function buildStatusMarkdown(status: string): string {
  return `**状态**\n${status.trim()}`
}

export function buildSessionMarkdown(sessionId: string | undefined): string | undefined {
  const normalized = (sessionId ?? "").trim()
  if (!normalized) return undefined
  return `**会话ID**\n\`${normalized}\``
}

// agent 文本原样转 markdown element 内容；空时显示 plugin 占位（避免 plugin 编造 agent 内容）。
export function buildReplyMarkdown(replyText: string): string {
  return normalizeBlockMarkdown(replyText) || EMPTY_REPLY_PLACEHOLDER
}

export function buildDetailsElement(detailsMarkdown: string | undefined): Record<string, unknown> | undefined {
  const content = normalizeBlockMarkdown(detailsMarkdown ?? "")
  if (!content) return undefined

  return {
    tag: "collapsible_panel",
    element_id: DETAILS_ELEMENT_ID,
    expanded: false,
    header: {
      title: {
        tag: "plain_text",
        content: "详细步骤",
      },
    },
    elements: [
      buildMarkdownElement(DETAILS_CONTENT_ELEMENT_ID, content),
    ],
  }
}

export function buildActionsElement(actions: readonly ReplyCardAction[]): Record<string, unknown> | undefined {
  if (actions.length === 0) return undefined

  return {
    tag: "column_set",
    element_id: ACTIONS_ELEMENT_ID,
    flex_mode: "none",
    background_style: "default",
    columns: actions.map((action) => ({
      tag: "column",
      width: "weighted",
      weight: 1,
      elements: [{
        tag: "button",
        text: { tag: "plain_text", content: action.text },
        type: action.style,
        value: action.value,
        ...(action.disabled ? { disabled: true } : {}),
      }],
    })),
  }
}

export function buildSimpleFallbackText(view: ReplyCardView): string {
  // simple 模式（CardKit 不可用降级）下用纯文本结构。
  // 不给 agent 文本贴语义标签——直接渲染原文，空时用 plugin 占位描述。
  const sections = [
    `【${view.title.trim() || DEFAULT_TITLE}】`,
    `状态：${view.compactStatus}`,
    view.sessionId ? `会话ID：${view.sessionId}` : "",
    normalizeBlockMarkdown(view.replyText) || EMPTY_REPLY_PLACEHOLDER,
  ].filter(Boolean)

  const details = normalizeBlockMarkdown(view.detailsMarkdown ?? "")
  if (details) {
    sections.push(`详细步骤：\n${details}`)
  }

  if (view.terminalState) {
    // 用本地化的 compactStatus 文案，避免把 enum 原值（completed/failed/aborted/…）泄露到用户可读面。
    sections.push(`终态：${buildCompactStatus(view.terminalState)}`)
  }

  return truncateMarkdown(cleanMarkdown(sections.join("\n\n")))
}

function buildMarkdownElement(elementId: string, content: string): Record<string, unknown> {
  return {
    tag: "markdown",
    element_id: elementId,
    content,
  }
}

function normalizeBlockMarkdown(content: string): string {
  const cleaned = cleanMarkdown(content)
    .replace(/\r/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()

  return cleaned ? truncateMarkdown(cleaned) : ""
}

function formatPhaseIcon(status: DetailPhaseStatus): string {
  switch (status) {
    case "completed":
      return "✅"
    case "error":
      return "❌"
    default:
      return "🔄"
  }
}
